import React, { useState } from 'react';
import { usePDF } from '../context/PDFContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';
import { toast } from 'sonner';
import TextTool from './tools/TextTool';
import EraseTool from './tools/EraseTool';
import MergeTool from './tools/MergeTool';
import SplitTool from './tools/SplitTool';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RightSidebar = () => {
  const { 
    currentTool, pdfInfo, selectedPages, setPdfInfo, setPages, setIsProcessing, setProcessingMessage,
    textElements, setTextElements, selectedTextId, setSelectedTextId, pendingText, setPendingText,
    imageElements, setImageElements, selectedImageId, setSelectedImageId, pendingImage, setPendingImage,
    highlightConfig, setHighlightConfig, pushUndo
  } = usePDF();
  const [rotateAngle, setRotateAngle] = useState(90);
  const [watermarkText, setWatermarkText] = useState('');
  const [signatureMode, setSignatureMode] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const [selectedFont, setSelectedFont] = useState('times-new-roman');
  const signatureCanvasRef = React.useRef(null);
  const [isSignDrawing, setIsSignDrawing] = useState(false);

  const SIGNATURE_FONTS = [
    { value: 'times-new-roman', label: 'Times New Roman', style: "'Times New Roman', PDFTimes, serif" },
    { value: 'dancing-script', label: 'Dancing Script', style: 'Dancing Script, cursive' },
    { value: 'pacifico', label: 'Pacifico', style: 'Pacifico, cursive' },
    { value: 'great-vibes', label: 'Great Vibes', style: 'Great Vibes, cursive' },
    { value: 'allura', label: 'Allura', style: 'Allura, cursive' },
    { value: 'sacramento', label: 'Sacramento', style: 'Sacramento, cursive' },
  ];

  // --- Text handlers ---
  const handleAddText = (textData) => setPendingText(textData);
  const handleUpdateText = (id, updates) => {
    pushUndo();
    setTextElements(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  };
  const handleDeleteText = (id) => {
    pushUndo();
    setTextElements(prev => prev.filter(t => t.id !== id));
    setSelectedTextId(null);
  };

  // --- Image handlers ---
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new window.Image();
      img.onload = () => {
        // Box adopts the image's true (EXIF-corrected) aspect ratio
        const maxDim = 200;
        const ratio = Math.min(maxDim / img.naturalWidth, maxDim / img.naturalHeight, 1);
        setPendingImage({
          data: event.target.result,
          pdfW: Math.round(img.naturalWidth * ratio),
          pdfH: Math.round(img.naturalHeight * ratio),
        });
        toast.success('Image ready - click on PDF to place it');
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  // --- Signature handlers ---
  const startSignDraw = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#000';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsSignDrawing(true);
  };
  const drawSign = (e) => {
    if (!isSignDrawing) return;
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };
  const stopSignDraw = () => setIsSignDrawing(false);
  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  };
  const useDrawnSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    setPendingImage({ data: dataUrl, pdfW: 200, pdfH: 80 });
    toast.success('Signature ready - click on PDF to place it');
    clearSignature();
  };
  const useTypedSignature = () => {
    if (!typedName.trim()) { toast.error('Please enter your name'); return; }
    // Render typed signature to a canvas, then export as image
    const offscreen = document.createElement('canvas');
    offscreen.width = 400;
    offscreen.height = 100;
    const ctx = offscreen.getContext('2d');
    const font = SIGNATURE_FONTS.find(f => f.value === selectedFont);
    ctx.font = `48px ${font.style}`;
    ctx.fillStyle = '#000';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedName, 10, 55);
    const dataUrl = offscreen.toDataURL('image/png');
    setPendingImage({ data: dataUrl, pdfW: 250, pdfH: 70 });
    toast.success('Signature ready - click on PDF to place it');
  };

  // --- Backend operations ---
  const handleRotate = async () => {
    if (!pdfInfo) { toast.error('No PDF loaded'); return; }
    setIsProcessing(true);
    setProcessingMessage('Rotating pages...');
    try {
      const formData = new FormData();
      formData.append('file_id', pdfInfo.id);
      formData.append('pages', selectedPages.length > 0 ? selectedPages.join(',') : 'all');
      formData.append('angle', rotateAngle);
      const response = await axios.post(`${API}/pdf/rotate`, formData);
      setPdfInfo({ ...pdfInfo, id: response.data.id });
      const pagesResponse = await axios.get(`${API}/pdf/pages/${response.data.id}`);
      setPages(pagesResponse.data.pages);
      toast.success(`Rotated ${selectedPages.length > 0 ? selectedPages.length : 'all'} page(s) by ${rotateAngle}`);
    } catch (error) {
      toast.error(`Failed to rotate: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
    }
  };

  const handleDeletePages = async () => {
    if (!pdfInfo || selectedPages.length === 0) {
      toast.error('Please select pages to delete from the left sidebar');
      return;
    }
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file_id', pdfInfo.id);
      formData.append('pages', selectedPages.join(','));
      const response = await axios.post(`${API}/pdf/delete-pages`, formData);
      setPdfInfo({ ...pdfInfo, id: response.data.id });
      const pagesResponse = await axios.get(`${API}/pdf/pages/${response.data.id}`);
      setPages(pagesResponse.data.pages);
      toast.success(`Deleted ${selectedPages.length} page(s)`);
    } catch (error) {
      toast.error(`Failed to delete pages: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCompress = async () => {
    if (!pdfInfo) { toast.error('No PDF loaded'); return; }
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file_id', pdfInfo.id);
      const response = await axios.post(`${API}/pdf/compress`, formData);
      setPdfInfo({ ...pdfInfo, id: response.data.id });
      const pagesResponse = await axios.get(`${API}/pdf/pages/${response.data.id}`);
      setPages(pagesResponse.data.pages);
      toast.success('PDF compressed successfully');
    } catch (error) {
      toast.error(`Failed to compress: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWatermark = async () => {
    if (!pdfInfo || !watermarkText) { toast.error('Please enter watermark text'); return; }
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file_id', pdfInfo.id);
      formData.append('text', watermarkText);
      const response = await axios.post(`${API}/pdf/add-watermark`, formData);
      setPdfInfo({ ...pdfInfo, id: response.data.id });
      const pagesResponse = await axios.get(`${API}/pdf/pages/${response.data.id}`);
      setPages(pagesResponse.data.pages);
      toast.success('Watermark added');
      setWatermarkText('');
    } catch (error) {
      toast.error('Failed to add watermark');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderToolPanel = () => {
    switch (currentTool) {
      case 'split':
        return <SplitTool pdfInfo={pdfInfo} onSplitComplete={() => {}} />;

      case 'merge':
        return (
          <MergeTool
            currentPdfId={pdfInfo?.id}
            onMergeComplete={(newId, newPages) => {
              setPdfInfo({ ...pdfInfo, id: newId, num_pages: newPages.length });
              setPages(newPages);
            }}
          />
        );

      case 'rotate':
        return (
          <div className="space-y-4" data-testid="rotate-panel">
            <div>
              <Label>Rotation Angle: {rotateAngle}</Label>
              <Slider
                data-testid="rotate-angle-slider"
                value={[rotateAngle]}
                onValueChange={(value) => setRotateAngle(value[0])}
                min={90} max={270} step={90}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                {selectedPages.length > 0 ? `Selected ${selectedPages.length} page(s)` : 'All pages will be rotated'}
              </p>
            </div>
            <Button onClick={handleRotate} className="w-full" data-testid="rotate-execute-button">
              Rotate Pages
            </Button>
          </div>
        );

      case 'delete':
        return (
          <div className="space-y-4" data-testid="delete-panel">
            <div>
              <Label>Delete Selected Pages</Label>
              <p className="text-sm text-muted-foreground mt-1.5">
                {selectedPages.length > 0 ? `${selectedPages.length} page(s) selected` : 'No pages selected'}
              </p>
            </div>
            <Button onClick={handleDeletePages} variant="destructive" className="w-full" data-testid="delete-execute-button" disabled={selectedPages.length === 0}>
              Delete Pages
            </Button>
          </div>
        );

      case 'compress':
        return (
          <div className="space-y-4" data-testid="compress-panel">
            <div>
              <Label>Compress PDF</Label>
              <p className="text-sm text-muted-foreground mt-1.5">Reduce file size while maintaining quality</p>
            </div>
            <Button onClick={handleCompress} className="w-full" data-testid="compress-execute-button">
              Compress PDF
            </Button>
          </div>
        );

      case 'watermark':
        return (
          <div className="space-y-4" data-testid="watermark-panel">
            <div>
              <Label htmlFor="watermark-text">Watermark Text</Label>
              <Input
                id="watermark-text" data-testid="watermark-text-input"
                placeholder="Enter watermark text"
                value={watermarkText} onChange={(e) => setWatermarkText(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <Button onClick={handleWatermark} className="w-full" data-testid="watermark-execute-button">
              Add Watermark
            </Button>
          </div>
        );

      case 'text':
        return (
          <TextTool 
            textElements={textElements}
            onAddText={handleAddText}
            onUpdateText={handleUpdateText}
            onDeleteText={handleDeleteText}
            selectedTextId={selectedTextId}
            onSelectText={setSelectedTextId}
          />
        );

      case 'erase':
        return <EraseTool />;

      case 'image':
        return (
          <div className="space-y-4" data-testid="image-tool">
            <div>
              <Label htmlFor="image-upload">Upload Image</Label>
              <label htmlFor="image-upload" className="cursor-pointer block mt-1.5">
                <div className="border-2 border-dashed border-border rounded-md p-4 hover:border-primary transition-colors text-center">
                  <p className="text-sm text-muted-foreground">Click to upload image</p>
                </div>
              </label>
              <input id="image-upload" data-testid="image-upload-input" type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
            </div>
            {pendingImage && (
              <div className="border border-border rounded-md p-2 bg-accent/50">
                <img src={pendingImage.data} alt="Preview" className="w-full h-auto rounded" />
                <p className="text-xs text-muted-foreground text-center mt-2">Click on PDF to place this image</p>
              </div>
            )}
            {imageElements.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs">{imageElements.length} image(s) placed</Label>
                {selectedImageId && (() => {
                  const img = imageElements.find(i => i.id === selectedImageId);
                  if (!img) return null;
                  return (
                    <div className="space-y-3 border-t pt-3">
                      <div>
                        <Label>Width: {img.width}px</Label>
                        <Slider value={[img.width]} onValueChange={(v) => setImageElements(prev => prev.map(i => i.id === selectedImageId ? {...i, width: v[0]} : i))} min={30} max={800} step={10} className="mt-1" />
                      </div>
                      <div>
                        <Label>Height: {img.height}px</Label>
                        <Slider value={[img.height]} onValueChange={(v) => setImageElements(prev => prev.map(i => i.id === selectedImageId ? {...i, height: v[0]} : i))} min={30} max={800} step={10} className="mt-1" />
                      </div>
                      <Button variant="destructive" size="sm" className="w-full" onClick={() => { setImageElements(prev => prev.filter(i => i.id !== selectedImageId)); setSelectedImageId(null); }}>
                        Delete Image
                      </Button>
                    </div>
                  );
                })()}
              </div>
            )}
            <div className="bg-accent/50 p-3 rounded-md">
              <p className="text-xs text-muted-foreground">1. Upload an image  2. Click on PDF to place  3. Drag to reposition  4. Click Save Images in toolbar</p>
            </div>
          </div>
        );

      case 'highlight':
        return (
          <div className="space-y-4" data-testid="highlight-tool">
            <div>
              <Label htmlFor="highlight-color">Highlight Color</Label>
              <Input id="highlight-color" data-testid="highlight-color-input" type="color" value={highlightConfig.color} onChange={(e) => setHighlightConfig(c => ({...c, color: e.target.value}))} className="mt-1.5 h-10" />
            </div>
            <div>
              <Label>Opacity: {Math.round(highlightConfig.opacity * 100)}%</Label>
              <Slider data-testid="highlight-opacity-slider" value={[highlightConfig.opacity]} onValueChange={(v) => setHighlightConfig(c => ({...c, opacity: v[0]}))} min={0.1} max={1} step={0.1} className="mt-2" />
            </div>
            <p className="text-sm text-muted-foreground">Drag on the PDF to highlight an area</p>
          </div>
        );

      case 'signature':
        return (
          <div className="space-y-4" data-testid="signature-tool">
            <div>
              <Label>Signature Mode</Label>
              <div className="flex gap-2 mt-1.5">
                <Button variant={signatureMode === 'draw' ? 'default' : 'outline'} className="flex-1" onClick={() => setSignatureMode('draw')}>Draw</Button>
                <Button variant={signatureMode === 'type' ? 'default' : 'outline'} className="flex-1" onClick={() => setSignatureMode('type')}>Type</Button>
              </div>
            </div>
            {signatureMode === 'draw' ? (
              <>
                <div>
                  <Label>Draw Your Signature</Label>
                  <canvas ref={signatureCanvasRef} data-testid="signature-canvas" width={240} height={100} className="border border-border rounded mt-2 bg-white cursor-crosshair w-full" onMouseDown={startSignDraw} onMouseMove={drawSign} onMouseUp={stopSignDraw} onMouseLeave={stopSignDraw} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={clearSignature} variant="outline" className="flex-1" data-testid="clear-signature-button">Clear</Button>
                  <Button onClick={useDrawnSignature} className="flex-1" data-testid="save-signature-button">Use Signature</Button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="typed-name">Your Name</Label>
                  <input id="typed-name" type="text" value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="John Doe" className="w-full mt-1.5 px-3 py-2 border border-border rounded-md bg-background text-foreground" />
                </div>
                <div>
                  <Label>Font Style</Label>
                  <div className="grid grid-cols-1 gap-1.5 mt-1.5">
                    {SIGNATURE_FONTS.map(font => (
                      <button key={font.value} onClick={() => setSelectedFont(font.value)} className={`text-left px-3 py-2 rounded border ${selectedFont === font.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                        <span style={{ fontFamily: font.style, fontSize: '20px' }}>{typedName || font.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <Button onClick={useTypedSignature} className="w-full" data-testid="use-typed-signature-button">Use Signature</Button>
              </>
            )}
            {pendingImage && (
              <div className="border border-primary rounded-md p-2 bg-primary/5">
                <p className="text-xs text-primary text-center">Signature ready - click on PDF to place it</p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">Select a tool to get started</p>
          </div>
        );
    }
  };

  if (!pdfInfo) return null;

  return (
    <div data-testid="right-sidebar" className="w-72 border-l border-border bg-background flex flex-col h-full">
      <div className="p-4 border-b border-border">
        <h3 className="text-xs font-semibold tracking-[0.05em] uppercase text-muted-foreground">
          {currentTool ? currentTool.replace('-', ' ').toUpperCase() : 'PROPERTIES'}
        </h3>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4">{renderToolPanel()}</div>
      </ScrollArea>
    </div>
  );
};

export default RightSidebar;
